import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {AppState} from "../../../shared/state/app.state";
import {ProjectService} from "../../data-access/project.service";
import {catchError, map, switchMap} from "rxjs/operators";
import {of, tap} from "rxjs";
import {
  addProject,
  addProjectFailure,
  addProjectSuccess, loadProjects, loadProjectsFailure, loadProjectsSuccess,
  modifyProject, modifyProjectFailure, modifyProjectSuccess,
  removeProject, removeProjectFailure,
  removeProjectSuccess, verifyDeleteProjectIsValid, verifyDeleteProjectIsValidFailure, verifyDeleteProjectIsValidSuccess
} from "./project.action";

import {ToastMessageOptions, MessageService} from "primeng/api";
import {GetMessage} from "../../../global-config/utils/toast-messages.utils";


@Injectable()
export class ProjectEffects {
  loadProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProjects),
      switchMap(() =>
        this.projectService.get()
          .pipe(
            map((projects) => loadProjectsSuccess({projects: projects})),
            catchError((error) => of(loadProjectsFailure({ error })))
          )
      )
    )
  );

  createProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addProject),
      switchMap((action) =>
        this.projectService.create(action.project)
          .pipe(
            map((addedProject) => addProjectSuccess({project: addedProject, successMessage: GetMessage('AddSuccess', 'Project')})),
            catchError((error) => of(addProjectFailure({ error: GetMessage('AddFailure', 'Project') })))
          )
      )
    )
  );

  modifyProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyProject),
      switchMap((action) =>
        this.projectService.update(action.project)
          .pipe(
            map((project) => modifyProjectSuccess({successMessage: GetMessage('ModifySuccess', 'Project')})),
            catchError((error) => of(modifyProjectFailure({ error: GetMessage('ModifyFailure', 'Project') })))
          )
      )
    )
  );

  removeProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeProject),
      switchMap((action) =>
        this.projectService.remove(action.projectId)
          .pipe(
            map((removedProjectId) => removeProjectSuccess({projectId: removedProjectId, successMessage: GetMessage('RemoveSuccess', 'Project')})),
            catchError((error) => of(removeProjectFailure({ error: GetMessage('RemoveFailure', 'Project') })))
          )
      )
    )
  );

  verifyDeleteIsValid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyDeleteProjectIsValid),
      switchMap((action) =>
        this.projectService.verifyDeleteIsValid(action.projectId)
          .pipe(
            map((verifyDeleteIsValidResponse) => verifyDeleteProjectIsValidSuccess({verifyDeleteIsValidResponse})),
            catchError((error) => of(verifyDeleteProjectIsValidFailure({ error: GetMessage('RemoveFailure', 'Project') })))
          )
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          removeProjectSuccess,
          modifyProjectSuccess,
          addProjectSuccess
        ),
        tap(({ successMessage }) => {
            return this.messageService.add({
              severity: 'success',
              summary: successMessage,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          removeProjectFailure,
          modifyProjectFailure,
          addProjectFailure
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private projectService: ProjectService,
    private messageService: MessageService
  ) {}
}
